<template>
  <a-drawer
    :title="dramaDetail?.name"
    width="1100"
    :visible="visible"
    :closable="false"
    :footer-style="{ textAlign: 'right' }"
    @close="onClose"
  >
    <!-- 功能条 -->
    <div class="tool-view">
      <a-row class="tool-row">
        <a-col :span="18">
          <!-- 保留布局结构 -->
        </a-col>
        <a-col :span="6">
          <a-row>
            <!-- 操作 -->
            <a-col :span="6" class="tool-title"></a-col>
            <a-col :span="18" style="white-space: nowrap; text-align: right;">
              <a-button type="primary" @click="touchAdd"><plus-outlined /> 创建任务</a-button>
            </a-col>
          </a-row>
        </a-col>
      </a-row>
    </div>
    <!-- 列表 -->
    <a-table
      class="table-view"
      :data-source="dataSource"
      :columns="columns"
      :row-key="record => record.id"
      :pagination="pagination"
      :loading="isLoading"
      @change="handleTableChange"
    >
      <!-- 自定义行内容 -->
      <template #bodyCell="{ column, record }">
        <!-- 源视频版本 -->
        <template v-if="column.key === 'type'">
          {{ YoutubeVideoTypes.find(item => item.value === record.type)?.text || '-' }}
        </template>
        <!-- 目标语种 -->
        <template v-else-if="column.key === 'target_lang_id'">
          {{ languages.find(item => item.id === record.target_lang_id)?.name || '-' }}
        </template>
        <!-- 音色 -->
        <template v-else-if="column.key === 'speech_type'">
          {{ SpeechTypes.find(item => item.value === record.speech_type)?.text || '-' }}
        </template>
        <!-- 长视频 -->
        <template v-else-if="column.key === 'long_status'">
          <!-- 进行中 -->
          <template v-if="record.long_status === VideoExportStatus.processing.value">
            <a-badge :color="VideoExportStatus.processing.color" :text="VideoExportStatus.processing.text"></a-badge>
          </template>
          <!-- 成功 -->
          <template v-else-if="record.long_status === VideoExportStatus.success.value">
            <a @click="touchPreview(record)">预览</a>
            <span v-if="!record.sfx_status" style="color: #999; margin-left: 4px;">（无音效）</span>
          </template>
          <!-- 失败 -->
          <template v-else-if="record.long_status === VideoExportStatus.failed.value">
            <a-badge :color="VideoExportStatus.failed.color" :text="VideoExportStatus.failed.text"></a-badge>
          </template>
          <!-- 未处理 -->
          <template v-else>
             <a-badge :color="VideoExportStatus.none.color" text="未合成"></a-badge>
          </template>
        </template>
        <!-- 操作 -->
        <template v-else-if="column.key === 'operation'">
          <a class="operation-item" @click="touchTask(record)">查看任务</a>
          <a-dropdown>
            <a class="operation-item">合并视频 <down-outlined /></a>
            <template #overlay>
              <a-menu @click="({ key }) => handleMergeClick(key, record)">
                <a-menu-item key="direct">直接合并</a-menu-item>
                <a-menu-item key="sfx">音效合并</a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
          <a 
            :class="['operation-item', { 'operation-item-disabled': record.long_status !== VideoExportStatus.success.value }]"
            @click="record.long_status === VideoExportStatus.success.value && touchPublishToYoutube(record)"
          >
            发布至油管
          </a>
        </template>
        <template v-else>
          {{ record[column.key] || '-' }}
        </template>
      </template>
    </a-table>
    
    <!-- 加载 -->
    <Loading :loading="isLoading"></Loading>
    <!-- 手动添加内容弹窗 -->
    <VideoExportAdd ref="RefVideoExportAdd" @success="getList"></VideoExportAdd>
    <!-- 视频预览 -->
    <PreviewVideoModal ref="RefPreviewModal"></PreviewVideoModal>
    <!-- 查看任务 -->
    <Viewtasks ref="RefViewtasks" @success="getList"></Viewtasks>
    <!-- 发布至油管 -->
    <PublishToYoutube ref="RefPublishToYoutube" @success="getList"></PublishToYoutube>
  </a-drawer>
</template>

<script setup>
import { onBeforeMount, reactive, ref } from 'vue'
import { message, Modal } from 'ant-design-vue'
// 引入 dayjs 及 扩展函数
import dayjs from 'dayjs'
// 引入公共方法
import Pub from '@/utils/public'
import Loading from '@/components/Loading'
// 引入视频预览组件
import PreviewVideoModal from '@/components/preview/PreviewVideoModal.vue'
// 引入油管内容弹窗组件
import VideoExportAdd from './VideoExportAdd'
// 引入查看任务弹窗组件
import Viewtasks from './ViewTasks'
// 引入发布至油管弹窗组件
import PublishToYoutube from './PublishToYoutube'
// 引入常量列表
import { VideoExportStatus, YoutubeVideoTypes, SpeechTypes } from '@/utils/constantList'
// 引入 API
import { taskList, taskLongVideo, langSearchList } from '@/api/admin'


// emit
const emit = defineEmits(['success'])
// 油管内容弹窗引用
let RefVideoExportAdd = ref(null)
// 查看任务弹窗引用
let RefViewtasks = ref(null)
// 发布至油管弹窗引用
let RefPublishToYoutube = ref(null)
// 视频预览引用
let RefPreviewModal = ref(null)
// 显示
let visible = ref(false)
// 加载
let isLoading = ref(false)
// 剧集对象
let dramaDetail = ref({})
// 数据源
let dataSource = ref([])
// 语种列表
let languages = ref([])
// 分页信息
let pagination = reactive({
  total: 0,
  current: 1,
  pageSize: 10,
  showSizeChanger: true
})
// 列数据源 - 根据图片中的字段标题
let columns = ref([
  {
    title: '任务ID',
    dataIndex: 'id',
    key: 'id'
  },
  {
    title: '源视频版本',
    dataIndex: 'type',
    key: 'type'
  },
  {
    title: '目标语种',
    dataIndex: 'target_lang_id',
    key: 'target_lang_id'
  },
  {
    title: '音色',
    dataIndex: 'speech_type',
    key: 'speech_type'
  },
  {
    title: '翻译方式',
    dataIndex: 'translation_type_list',
    key: 'translation_type_list'
  },
  {
    title: '长视频',
    dataIndex: 'long_status',
    key: 'long_status'
  },
  {
    title: '创建人',
    dataIndex: 'admin_name',
    key: 'admin_name'
  },
  {
    title: '创建日期',
    dataIndex: 'created_date',
    key: 'created_date'
  },
  {
    title: '操作',
    dataIndex: 'operation',
    key: 'operation',
    width: 190
  }
])

// 显示弹窗
function showDrawer(drama) {
  visible.value = true
  dramaDetail.value = drama || {}
  // 获取界面语种列表
  getLanguageList()
  // 初始化获取列表
  getList()
}

// 获取界面语种列表
function getLanguageList () {
  langSearchList().then(res => {
    isLoading.value = false
    const { code, msg, data } = res
    if (code === 0) {
      languages.value = data
    } else {
      message.error(res.message || msg)
    }
  })
}

// 分页处理
function handleTableChange(p, filters, sorter) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  // 获取列表
  getList()
}

// 新建
function touchAdd() {
  // 显示手动添加内容弹窗
  RefVideoExportAdd.value.showDrawer(dramaDetail.value)
}

// 查看任务
function touchTask(record) {
  // 显示查看任务弹窗，传入记录数据
  RefViewtasks.value.showDrawer(dramaDetail.value, record)
}

// 预览
function touchPreview (record) {
  RefPreviewModal.value.showModal(record.long_video_url, dramaDetail.value?.name)
}

// 合并视频 - 下拉菜单点击
function handleMergeClick(key, record) {
  const isSfx = key === 'sfx'
  if (record.long_status === VideoExportStatus.processing.value) {
    Modal.info({
      title: '合并任务正在进行中',
      content: '请勿重复操作',
      okText: '好的',
      cancelButtonProps: { style: { display: 'none' } }
    })
    return
  }
  if (record.long_status === VideoExportStatus.success.value) {
    Modal.confirm({
      title: '已合并过长视频，确认要重新合并吗？',
      okText: '是',
      cancelText: '否',
      onOk: () => doMergeVideo(record, isSfx)
    })
  } else {
    doMergeVideo(record, isSfx)
  }
}

// 合并视频 - 执行合并
function doMergeVideo(record, isSfx = false) {
  const params = { task_id: record.id, sfx_status: isSfx ? 1 : 0 }
  
  taskLongVideo(params).then(res => {
    isLoading.value = false
    const { code, msg, data } = res
    if (code === 0) {
      message.success(res.message)
      getList()
    } else {
      message.error(res.message)
    }
  }).catch(() => {
    isLoading.value = false
    message.error('合并失败')
  })
}

// 发布至油管
function touchPublishToYoutube(record) {
  // 显示发布至油管弹窗，传入记录数据
  RefPublishToYoutube.value.showDrawer(record, dramaDetail.value)
}

// 获取列表
function getList() {
  isLoading.value = true
  const params = {
    drama_id: dramaDetail.value.id,
    page: pagination.current,
    page_size: pagination.pageSize
  }
  taskList(params).then(res => {
    isLoading.value = false
    const { code, data } = res
    if (code === 0) {
      const list = data.data || []
      list.forEach(item => {
        const videoType = YoutubeVideoTypes.find(vt => vt.value === item.type)?.text || '-'
        const targetLang = languages.value.find(lang => lang.id === item.target_lang_id)?.name || '-'
        item.type2target_text = `${videoType} → ${targetLang}`
      })
      dataSource.value = list
      pagination.total = data.total
    }
  }).catch(() => {
    isLoading.value = false
  })
}

// 关闭弹窗
function onClose() {
  if (!isLoading.value) {
    visible.value = false
    dramaDetail.value = null
    // 重置分页
    pagination.current = 1
    pagination.total = 0
    dataSource.value = []
  }
}

// 暴露方法给父组件
defineExpose({
  showDrawer
})
</script>

<style scoped>
.tool-row {
  margin-bottom: 16px;
}

.tool-title {
  text-align: right;
}

.tool-item {
  width: 100%;
}

.table-view {
  background: #fff;
  border-radius: 6px;
}

.operation-item {
  color: #1890ff;
  margin-right: 16px;
  cursor: pointer;
}

.operation-item:hover {
  color: #40a9ff;
}

.operation-item-disabled {
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}

.operation-item-disabled:hover {
  color: rgba(0, 0, 0, 0.25);
}
</style>
